// @ts-strict-ignore
import { BufferGeometry, CircleGeometry, Matrix4, Mesh, MeshBasicMaterial, MeshPhongMaterial, Object3D, PlaneGeometry, Vector3 } from 'three';
import { WxConnectionElement } from './wx-connection-element';
import { WxText } from './shape/wx-text';
import { ExportToolUtils } from './export-tool-utils';
import { WxRectangle } from './shape/wx-rectangle';
import { WxArc } from './shape/wx-arc';
import { MetaElementVO } from '@shared/vo/meta-element.vo';
import { CommonConstants } from '@3d-scene/constants/common-constants';
import { Rectangle3D } from '@3d-scene/model/rectangle-3d';
import { ArticlesCreator } from '@project/components/article/presenter/article-creator';

export class WxConnectionPlaneMesh extends Object3D {

  public firstFlatMesh: Mesh;
  public connectionElement: WxConnectionElement;
  private _connectionName: string;

  public get connectionName(): string {
    return this._connectionName;
  }

  public set connectionName(value: string) {
    this._connectionName = value;
    if (this.firstFlatMesh !== null) {
      this.firstFlatMesh.name = this._connectionName;
    }
  }

  public constructor(connectionElement: WxConnectionElement) {
    super();
    if (!connectionElement) {
      return;
    }
    this.connectionElement = connectionElement;
    this._connectionName = MetaElementVO.CONNECTION_PREFIX + connectionElement.connectionId;
  }

  public async invalidateMesh(exportToolUtils: ExportToolUtils): Promise<void> {
    const defaultRotationMatrix: Matrix4 = new Matrix4().makeRotationAxis(new Vector3(1, 0, 0), -Math.PI / 2);

    let mesh: Mesh;
    for (const drawingElement of this.connectionElement.drawingElements) {
      const material: MeshPhongMaterial = ExportToolUtils.createColorMaterial(drawingElement.rgb1);
      const scaleXYZ: Vector3 = ExportToolUtils.translateToConfiguratorCoords(drawingElement.mirrorX, drawingElement.mirrorY, drawingElement.mirrorZ);
      for (const shapeElement of drawingElement.elements) {
        const points: Vector3[] = shapeElement.getPoints(true);
        if (drawingElement.mirror) {
          points.reverse();
        }
        let isFlatMesh: boolean = false;

        if (shapeElement instanceof WxText) {
          mesh = await exportToolUtils.createTextMesh(shapeElement, drawingElement, true).toPromise();
          isFlatMesh = true;
        } else if (points.length > 2) {
          if (scaleXYZ.length() < CommonConstants.EPSILON) {
            if (shapeElement instanceof WxRectangle) {
              mesh = this.createRectangleMesh(points, material);
            } else if (shapeElement instanceof WxArc) {
              mesh = this.createCircleMesh(shapeElement.getRadius(), material);
            } else {
              mesh = ExportToolUtils.createExtrudeMesh(points, scaleXYZ, shapeElement.closed, material);
            }
            isFlatMesh = true;
          } else {
            mesh = ExportToolUtils.createExtrudeMesh(points, scaleXYZ, shapeElement.closed, material);
            isFlatMesh = false;
          }
        }

        if (mesh) {
          if (!this.firstFlatMesh && isFlatMesh) {
            this.firstFlatMesh = mesh as Mesh;
            this.firstFlatMesh.name = this._connectionName;
          } else {
            mesh.name = 's' + drawingElement.drawingID + '-' + drawingElement.elementOrder;
          }

          const transformMatrix: Matrix4 = ExportToolUtils.translateToConfiguratorTransformation(drawingElement.coordinateX,
            drawingElement.coordinateY, drawingElement.coordinateZ, drawingElement.rotationX, drawingElement.rotationY, drawingElement.rotationZ);
          transformMatrix.multiply(defaultRotationMatrix);
          mesh.applyMatrix4(transformMatrix);

          this.add(mesh);
        }
      }
    }
    if (!this.firstFlatMesh) {
      const planeBufferGeometry: PlaneGeometry = new PlaneGeometry(10, 10);
      this.firstFlatMesh = new Mesh(planeBufferGeometry, new MeshBasicMaterial({color: 0xFFFFFF}));
      this.firstFlatMesh.name = this._connectionName;
      this.firstFlatMesh.applyMatrix4(defaultRotationMatrix);
      this.add(this.firstFlatMesh);
    }
    return;
  }

  private createRectangleMesh(points: Vector3[], material: MeshPhongMaterial): Mesh {
    const rectangle: Rectangle3D = Rectangle3D.fromArray(points);
    const mesh: Mesh = ArticlesCreator.createPlaneForRectangle(rectangle);
    mesh.material = material;
    return mesh;
  }

  private createCircleMesh(radius: number, material: MeshPhongMaterial): Mesh {
    const circleGeometry: CircleGeometry = new CircleGeometry(radius, 32, Math.PI, Math.PI * 2);
    const geometry = new BufferGeometry().copy(circleGeometry);
    geometry.computeVertexNormals();
    return new Mesh(geometry, material);
  }

  public clone(recursive?: boolean): this {
    const clone = super.clone(recursive);
    clone.connectionElement = this.connectionElement;
    clone.firstFlatMesh = clone.children.find(mesh => mesh.name === this.firstFlatMesh.name) as Mesh;
    clone.connectionName = this.connectionName;
    return clone;
  }

}
